import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import { FiHome, FiBookOpen, FiAward, FiTool, FiClock, FiUser, FiCpu } from 'react-icons/fi';
import { useAppContext } from '../context/AppContext';

const NavItem = ({ to, icon: Icon, label }) => {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `flex flex-col md:flex-row items-center md:justify-start justify-center gap-1 md:gap-3 p-2 md:px-4 md:py-3 rounded-xl transition-all duration-200 ${
          isActive 
            ? 'bg-blue-600/20 text-blue-400 md:border-l-4 md:border-blue-500 md:rounded-l-none' 
            : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
        }`
      }
    >
      <Icon className="text-xl md:text-lg mb-1 md:mb-0" />
      <span className="text-[10px] md:text-sm font-medium">{label}</span>
    </NavLink>
  );
};

export const Layout = () => {
  const { user } = useAppContext();
  const location = useLocation();
  const isTroubleshootFlow = location.pathname.includes('/troubleshoot');

  return (
    <div className="flex h-screen overflow-hidden bg-slate-950">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-slate-900 border-r border-slate-800 z-20">
        <div className="p-6 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-blue-600 flex items-center justify-center shadow-lg shadow-blue-600/20">
            <FiCpu className="text-white text-xl" />
          </div>
          <span className="text-xl font-bold bg-gradient-to-r from-white to-slate-400 bg-clip-text text-transparent">AdamAI</span>
        </div>
        
        <div className="px-6 pb-6">
          <div className="p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 flex items-center gap-3">
            <img src={user.avatar} alt="User" className="w-10 h-10 rounded-full border border-slate-600" />
            <div>
              <p className="text-sm font-medium text-white">{user.name}</p>
              <p className="text-xs text-blue-400">Level {user.level} Tech</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 px-4 space-y-1 overflow-y-auto">
          <NavItem to="/" icon={FiHome} label="Dashboard" />
          <NavItem to="/academy" icon={FiBookOpen} label="Academy" />
          <NavItem to="/certifications" icon={FiAward} label="Certifications" />
          
          <div className="pt-4 pb-2 px-4">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Field Operations</p>
          </div>
          
          <NavItem to="/troubleshoot" icon={FiTool} label="Troubleshoot" />
          <NavItem to="/history" icon={FiClock} label="Case History" />
          <NavItem to="/profile" icon={FiUser} label="Profile" />
        </nav>
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col h-full relative z-10">
        {/* Mobile Header */}
        <header className="md:hidden bg-slate-900/80 backdrop-blur-md border-b border-slate-800 p-4 flex justify-between items-center sticky top-0 z-30">
          <div className="flex items-center gap-2">
             <div className="w-7 h-7 rounded-md bg-blue-600 flex items-center justify-center">
              <FiCpu className="text-white text-sm" />
            </div>
            <span className="text-lg font-bold text-white">AdamAI</span>
          </div>
          <img src={user.avatar} alt="User" className="w-8 h-8 rounded-full border border-slate-600" />
        </header>

        <div className="flex-1 overflow-y-auto pb-20 md:pb-0 scroll-smooth">
          <div className="p-4 md:p-8 max-w-5xl mx-auto w-full">
            <Outlet />
          </div>
        </div>
      </main>

      {/* Mobile Bottom Nav - Hide when deep in troubleshoot flow to save screen space */}
      {!isTroubleshootFlow && (
        <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-lg border-t border-slate-800 flex justify-around p-2 pb-safe z-40">
          <NavItem to="/" icon={FiHome} label="Home" />
          <NavItem to="/academy" icon={FiBookOpen} label="Academy" />
          <NavItem to="/troubleshoot" icon={FiTool} label="Fix It" />
          <NavItem to="/history" icon={FiClock} label="History" />
          <NavItem to="/profile" icon={FiUser} label="Profile" />
        </nav>
      )}
    </div>
  );
};